import React, { useState, useEffect, useMemo } from 'react';
import { taskAPI } from '../../../api/services';
import { useAppSelector } from '../../../store/hooks';
import { useNavigate } from 'react-router-dom';
import NotificationPanel from '../../Common/NotificationPanel';

const STATUS_DOT: Record<string, string> = {
  todo: 'bg-gray-400', in_progress: 'bg-blue-500', blocked: 'bg-red-500',
  review: 'bg-yellow-500', done: 'bg-green-500',
};
const PRIORITY_PILL: Record<string, string> = {
  critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white',
  medium: 'bg-yellow-400 text-gray-900', low: 'bg-gray-200 text-gray-600',
};

const QuickActions: React.FC<{ onTaskClick?: (taskId: string) => void }> = ({ onTaskClick }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector(s => s.auth);
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    taskAPI.getMyTasks().then(r => {
      const data = r.data.data ?? r.data ?? [];
      setTasks(Array.isArray(data) ? data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  // Group tasks by project → parent (feature or folder) → tasks
  const grouped = useMemo(() => {
    const projects: Record<string, { name: string; groups: Record<string, { name: string; type: 'feature' | 'folder'; tasks: any[] }>; standalone: any[] }> = {};
    const noProject: any[] = [];

    for (const t of tasks) {
      if (!t.projectId) { noProject.push(t); continue; }
      const pid = typeof t.projectId === 'object' ? t.projectId._id : t.projectId;
      const pName = typeof t.projectId === 'object' ? t.projectId.name : 'Project';
      if (!projects[pid]) projects[pid] = { name: pName, groups: {}, standalone: [] };

      if (t.parentType === 'feature' && t.parentId) {
        const fid = typeof t.parentId === 'object' ? t.parentId._id || t.parentId : t.parentId;
        const fName = typeof t.featureId === 'object' && t.featureId?.name ? t.featureId.name : (t.featureName || 'Feature');
        if (!projects[pid].groups[fid]) projects[pid].groups[fid] = { name: fName, type: 'feature', tasks: [] };
        projects[pid].groups[fid].tasks.push(t);
      } else if (t.parentType === 'folder' && t.parentId) {
        const fid = typeof t.parentId === 'object' ? t.parentId._id || t.parentId : t.parentId;
        if (!projects[pid].groups[fid]) projects[pid].groups[fid] = { name: t.folderName || 'Folder', type: 'folder', tasks: [] };
        projects[pid].groups[fid].tasks.push(t);
      } else {
        projects[pid].standalone.push(t);
      }
    }
    return { projects, noProject };
  }, [tasks]);

  const TaskRow = ({ task, indent = 0 }: { task: any; indent?: number }) => (
    <div
      className="grid grid-cols-12 gap-1 py-2 text-sm hover:bg-gray-50/80 cursor-pointer transition-colors"
      style={{ paddingLeft: `${20 + indent * 24}px`, paddingRight: '20px' }}
      onClick={() => onTaskClick ? onTaskClick(task._id) : navigate(`/employee/tasks/${task._id}`)}
    >
      <div className="col-span-5 flex items-center gap-2 min-w-0">
        <div className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[task.status] || 'bg-gray-300'}`} />
        <span className={`truncate ${task.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`} title={task.title}>{task.title}</span>
      </div>
      <div className="col-span-2 flex items-center">
        {(task.assignedTo ?? []).length > 0 ? (
          <div className="flex -space-x-1">
            {(task.assignedTo ?? []).slice(0, 2).map((a: any, i: number) => (
              <div key={i} className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center border border-white" title={a.name}>{(a.name || '?')[0]}</div>
            ))}
          </div>
        ) : <span className="text-xs text-gray-300">—</span>}
      </div>
      <div className="col-span-1">{task.priority && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_PILL[task.priority] || 'bg-gray-200'}`}>{task.priority}</span>}</div>
      <div className="col-span-1 text-xs text-gray-500 flex items-center">{fmtDate(task.dueDate)}</div>
      <div className="col-span-1 text-xs text-blue-600 font-medium flex items-center">{task.storyPoints > 0 ? task.storyPoints : ''}</div>
      <div className="col-span-1 text-xs text-gray-400 capitalize flex items-center">{(task.status || '').replace('_', ' ')}</div>
      <div className="col-span-1 text-xs text-gray-400 truncate flex items-center">{task.createdBy?.name?.split(' ')[0] || ''}</div>
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Welcome */}
      <div className="px-8 pt-8 pb-4">
        <h1 className="text-xl font-semibold text-gray-900">Welcome back, {user?.name?.split(' ')[0] || 'there'}</h1>
        <p className="text-sm text-gray-400 mt-1">Select a project from the sidebar, or review your tasks below.</p>
      </div>

      {/* Quick links */}
      <div className="px-8 pb-4 flex gap-3">
        <button onClick={() => navigate('/employee/projects/new')} className="text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors">
          + New Project
        </button>
        <button onClick={() => navigate('/knowledge-base')} className="text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors">
          Knowledge Base
        </button>
      </div>

      {/* Notifications */}
      <div className="px-8 pb-4">
        <NotificationPanel limit={5} />
      </div>

      {/* My Tasks — grouped by project → feature */}
      <div>
        <div className="px-8 pb-2">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">My Tasks</h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
        ) : tasks.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 px-8">No tasks assigned to you yet.</p>
        ) : (
          <div>
            {/* Column header */}
            <div className="grid grid-cols-12 gap-1 px-5 py-1.5 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
              <div className="col-span-5 pl-4">Name</div>
              <div className="col-span-2">Assignee</div>
              <div className="col-span-1">Priority</div>
              <div className="col-span-1">Due</div>
              <div className="col-span-1">SP</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-1">Owner</div>
            </div>

            {/* Projects */}
            {Object.entries(grouped.projects).map(([pid, proj]) => (
              <div key={pid}>
                {/* Project header */}
                <div className="flex items-center gap-2 px-5 py-2 mt-1 cursor-pointer hover:bg-gray-50/50" onClick={() => navigate(`/employee/projects/${pid}`)}>
                  <svg className="w-3.5 h-3.5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                  </svg>
                  <span className="text-xs font-bold text-gray-700">{proj.name}</span>
                  <span className="text-xs text-gray-400">{Object.values(proj.groups || {}).reduce((s: number, f: any) => s + f.tasks.length, 0) + proj.standalone.length}</span>
                </div>

                {/* Features & Folders inside project */}
                {Object.entries(proj.groups).map(([gid, group]) => (
                  <div key={gid}>
                    <div className={`flex items-center gap-2 py-1.5 cursor-pointer ${group.type === 'feature' ? 'hover:bg-purple-50/30' : 'hover:bg-blue-50/30'}`} style={{ paddingLeft: '32px' }}>
                      {group.type === 'feature' ? (
                        <svg className="w-3.5 h-3.5 text-purple-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                        </svg>
                      )}
                      <span className={`text-xs font-medium ${group.type === 'feature' ? 'text-purple-700' : 'text-blue-700'}`}>{group.name}</span>
                      <span className="text-xs text-gray-400">{group.tasks.length}</span>
                    </div>
                    {group.tasks.map(t => <TaskRow key={t._id} task={t} indent={2} />)}
                  </div>
                ))}

                {/* Standalone tasks in project */}
                {proj.standalone.map(t => <TaskRow key={t._id} task={t} indent={1} />)}
              </div>
            ))}

            {/* Tasks without project */}
            {grouped.noProject.length > 0 && (
              <div>
                <div className="flex items-center gap-2 px-5 py-2 mt-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="text-xs font-bold text-gray-500">Other</span>
                  <span className="text-xs text-gray-400">{grouped.noProject.length}</span>
                </div>
                {grouped.noProject.map(t => <TaskRow key={t._id} task={t} indent={1} />)}
              </div>
            )}

            {/* Summary */}
            <div className="px-5 py-2 text-xs text-gray-400 border-t border-gray-100">
              {tasks.length} tasks · SUM {tasks.reduce((s: number, t: any) => s + (t.storyPoints || 0), 0)} SP
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickActions;
